import React from 'react';
import { Briefcase, AlertTriangle, Scale, Package, CreditCard, Check, Award, Info, Plane } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета багажа
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Получаем данные о превышении норм
  const hasExcess = results.hasExcess;
  const { baggageWeightExcess, baggageCountExcess, carryOnWeightExcess, carryOnCountExcess } = results;
  const totalExcessFee = results.totalExcessFee;

  return (
    <ToolResult title="Результаты расчета багажа">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Допустимые нормы */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <Check className="w-4 h-4"/>
            Допустимые нормы багажа
          </h3>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Основной багаж:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {results.allowedBaggageWeight} кг × {results.allowedBaggageCount} {pluralize(results.allowedBaggageCount, 'место', 'места', 'мест')}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Ручная кладь:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {results.allowedCarryOnWeight} кг × {results.allowedCarryOnCount} {pluralize(results.allowedCarryOnCount, 'место', 'места', 'мест')}
              </span>
            </div>
            {results.loyaltyBonus && results.loyaltyBonus.extraWeight > 0 && (
              <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                <span>Бонус по программе лояльности:</span>
                <span className="font-medium">+{results.loyaltyBonus.extraWeight} кг</span>
              </div>
            )}
          </div>
        </div>

        {/* Стоимость перевеса */}
        <div className={`rounded-lg ${hasExcess 
            ? "bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 border-rose-100 dark:border-rose-800/50" 
            : "bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 border-gray-200 dark:border-gray-700"} 
            p-5 border shadow-sm`}>
          <h3 className={`text-base font-medium mb-1.5 flex items-center gap-1.5 ${hasExcess 
              ? "text-rose-700 dark:text-rose-300" 
              : "text-gray-700 dark:text-gray-300"}`}>
            <CreditCard className="w-4 h-4"/>
            {hasExcess ? "Стоимость перевеса" : "Нет превышения норм"}
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            {hasExcess ? (
              <>
                <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
                  {formatCurrency(totalExcessFee, 0)}
                </span>
                <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
                  {results.overweightFee > 0 && `(Перевес: ${formatCurrency(results.overweightFee, 0)})`}
                </span>
              </>
            ) : (
              <span className="text-3xl font-bold text-gray-800 dark:text-gray-200" aria-live="polite">
                0 ₽
              </span>
            )}
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            {hasExcess 
              ? "Включает стоимость перевеса и дополнительных мест багажа" 
              : "Ваш багаж соответствует нормам авиакомпании"}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Авиакомпания" 
          value={results.airline.name} 
          color="purple" 
          icon={<Plane className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Регистрируемый багаж" 
          value={`${results.effectiveBaggageWeight} кг × ${results.effectiveBaggageCount} ${pluralize(results.effectiveBaggageCount, 'место', 'места', 'мест')}`}
          color="blue" 
          icon={<Briefcase className="w-4 h-4"/>}
        />
        
        {baggageWeightExcess > 0 && (
          <ResultItem 
            label="Превышение веса багажа" 
            value={`${baggageWeightExcess} кг (${formatCurrency(results.overweightFee, 0)})`} 
            color="red" 
            icon={<AlertTriangle className="w-4 h-4"/>} 
          />
        )}
        
        {baggageCountExcess > 0 && (
          <ResultItem 
            label="Дополнительные места багажа" 
            value={`${baggageCountExcess} ${pluralize(baggageCountExcess, 'место', 'места', 'мест')} (${formatCurrency(results.extraPieceFee, 0)})`} 
            color="red" 
            icon={<AlertTriangle className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Ручная кладь" 
          value={`${results.effectiveCarryOnWeight} кг × ${results.effectiveCarryOnCount} ${pluralize(results.effectiveCarryOnCount, 'место', 'места', 'мест')}`}
          color="teal" 
          icon={<Package className="w-4 h-4"/>}
        />
        
        {(carryOnWeightExcess > 0 || carryOnCountExcess > 0) && (
          <ResultItem 
            label="Превышение ручной клади" 
            value={`${carryOnWeightExcess > 0 ? `${carryOnWeightExcess} кг` : ''} ${carryOnCountExcess > 0 ? `+${carryOnCountExcess} мест` : ''} (${formatCurrency(results.carryOnExcessFee, 0)})`} 
            color="red" 
            icon={<AlertTriangle className="w-4 h-4"/>} 
          />
        )}
        
        {results.loyaltyBonus && (results.loyaltyBonus.extraWeight > 0 || results.loyaltyBonus.extraCount > 0) && (
          <ResultItem 
            label="Бонус по программе лояльности" 
            value={`${results.loyaltyBonus.extraWeight > 0 ? `+${results.loyaltyBonus.extraWeight} кг` : ''} ${results.loyaltyBonus.extraCount > 0 ? `+${results.loyaltyBonus.extraCount} место` : ''}`} 
            color="amber" 
            icon={<Award className="w-4 h-4"/>} 
          />
        )}
        
        {results.extraItems && results.extraItems.length > 0 && (
          <ResultItem 
            label="Дополнительные предметы" 
            value={`${results.extraItems.length} ${pluralize(results.extraItems.length, 'предмет', 'предмета', 'предметов')} (${formatCurrency(results.extraItemsFee, 0)})`} 
            color="purple" 
            icon={<Package className="w-4 h-4"/>} 
          />
        )}
        
        {hasExcess && (
          <ResultItem 
            label="Итоговая стоимость" 
            value={formatCurrency(totalExcessFee, 0)} 
            color="red" 
            icon={<CreditCard className="w-4 h-4"/>} 
          />
        )}

        {/* Рекомендации */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Info className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Рекомендации</h4>
              <ul className="space-y-2 text-sm">
                {results.recommendations && results.recommendations.map((rec, index) => (
                  <li key={index} className={`flex items-start ${
                    rec.type === 'success' ? 'text-green-600 dark:text-green-400' :
                    rec.type === 'warning' ? 'text-amber-600 dark:text-amber-400' :
                    rec.type === 'error' ? 'text-red-600 dark:text-red-400' :
                    'text-blue-600 dark:text-blue-400'
                  }`}>
                    <span className="mr-2 mt-0.5">
                      {rec.type === 'success' ? <Check className="w-4 h-4" /> :
                       rec.type === 'warning' || rec.type === 'error' ? <AlertTriangle className="w-4 h-4" /> :
                       <Info className="w-4 h-4" />}
                    </span>
                    {rec.text}
                  </li>
                ))}
                
                {!results.recommendations || results.recommendations.length === 0 && (
                  <li className="text-gray-600 dark:text-gray-400">
                    Нет специальных рекомендаций по вашему багажу.
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return many;
  if (lastDigit === 1) return one;
  if (lastDigit >= 2 && lastDigit <= 4) return few;
  return many;
};

export default ResultsOverview;